/**
 * The TAF, as a band over the hours it covers plus the list that says it in words.
 *
 * The band is a picture and is hidden from assistive technology on purpose: the
 * list under it carries every fact the band draws, in the order the forecast
 * publishes them, and a screen reader reading out sixteen positioned `<span>`s
 * would carry none of them. The two are the same data (`tafBand` numbers both
 * from one array), so they cannot drift apart.
 *
 * Visibility is printed in metres and kilometres — the units SERA's minima are
 * written in — at the precision the decoding left it; a `9999`/CAVOK group says
 * "at least" and is printed that way rather than as a figure it does not
 * guarantee.
 */
import { useT } from '../../i18n';
import { visibMetric, zHour, zTime, type TafPeriod } from '../../wx';
import { CategoryDot } from './CategoryDot';
import { categoryAt, tafBand, type TafSegment } from './timeline';

/** Epoch ms → "18Z". `zHour` reads seconds when handed a number, so go through ISO. */
const zh = (ms: number): string => zHour(new Date(ms).toISOString());

/** Epoch ms → "11:10Z". A forecast is published on the hour; an ARRIVAL is not. */
const zm = (ms: number): string => zTime(new Date(ms).toISOString());

/** "12Z–15Z PROB30 TEMPO" — when a period applies and what kind of period it is. */
function whenOf(p: TafPeriod): string {
  const bits: string[] = [];
  const span = [zHour(p.from), zHour(p.to)].filter(Boolean).join('–');
  if (span) bits.push(span);
  // NOAA reports PROB30 as change "PROB" with probability 30, so printing both
  // spells "PROB30 PROB"; and PROB30 TEMPO is a real and different group
  if (p.probability !== null) bits.push('PROB' + p.probability);
  if (p.change && p.change !== 'PROB') bits.push(p.change);
  return bits.join(' ');
}

function detailOf(p: TafPeriod, t: ReturnType<typeof useT>): string {
  const out: string[] = [];
  if (p.visib !== null) {
    out.push(
      p.visibPlus
        ? t('wx.taf.visib.min', { v: '10 km' })
        : t('wx.taf.visib', { v: visibMetric(p.visib) }),
    );
  }
  if (p.ceilingFt !== null) out.push(t('wx.taf.ceiling', { ft: p.ceilingFt }));
  if (p.wx) out.push(p.wx);
  return out.join(' · ');
}

function Segment({ seg, title }: { seg: TafSegment; title: string }) {
  const key = (seg.category ?? 'none').toLowerCase();
  return (
    <span
      className={'wx-seg wx-seg-' + key}
      style={{ left: seg.x * 100 + '%', width: seg.w * 100 + '%' }}
      title={title}
    >
      {seg.wide && seg.category ? <span className="wx-seg-label">{seg.category}</span> : null}
    </span>
  );
}

export function TafTimeline({
  periods,
  validTo,
  etd,
  eta,
}: {
  periods: readonly TafPeriod[];
  validTo?: string | number | null | undefined;
  etd?: number | null | undefined;
  eta?: number | null | undefined;
}) {
  const t = useT();
  const band = tafBand(periods, { etd: etd ?? null, eta: eta ?? null });
  if (!band) return null;

  const title = (seg: TafSegment): string =>
    [whenOf(seg.period), seg.category ?? '', detailOf(seg.period, t)].filter(Boolean).join(' · ');

  const etaMark = band.marks.find(m => m.kind === 'eta') ?? null;
  const etaCat = categoryAt(band, etaMark?.at);
  const atEta = etaMark && etaCat ? { at: etaMark.at, cat: etaCat } : null;

  return (
    <div className="wx-taf">
      <div className="wx-taf-head">
        <span className="ae-eyebrow">TAF</span>
        {validTo ? (
          <span className="wx-taf-valid ae-num">{t('wx.taf.valid', { time: zHour(validTo) })}</span>
        ) : null}
      </div>

      <div className="wx-band" aria-hidden="true">
        <span className="wx-band-row wx-band-base">
          {band.base.map(s => (
            <Segment key={s.index} seg={s} title={title(s)} />
          ))}
        </span>
        {band.temp.length ? (
          <span className="wx-band-row wx-band-temp">
            {band.temp.map(s => (
              <Segment key={s.index} seg={s} title={title(s)} />
            ))}
          </span>
        ) : null}
        {band.marks.map(m => (
          <span
            key={m.kind}
            className={'wx-mark wx-mark-' + m.kind}
            style={{ left: m.x * 100 + '%' }}
            title={t('wx.tick.' + m.kind) + ' ' + zm(m.at)}
          />
        ))}
      </div>

      <div className="wx-band-axis ae-num">
        <span>{zh(band.from)}</span>
        <span>{zh(band.to)}</span>
      </div>

      {/* the one line the band exists for: what the forecast says at the hour the
          flight lands, worst-case over any temporary group that covers it */}
      {atEta ? (
        <p className="wx-at-eta">
          <span>{t('wx.taf.ateta', { time: zm(atEta.at) })}</span>
          <CategoryDot cat={atEta.cat} size="sm" />
        </p>
      ) : null}

      <ul className="wx-perlist">
        {[...band.base, ...band.temp]
          .sort((a, b) => a.index - b.index)
          .map(s => {
            const detail = detailOf(s.period, t);
            return (
              <li key={s.index}>
                {/* the dot has a column of its own so the times line up under
                    each other whether or not a group has a category */}
                <span className="wx-per-cat">
                  <CategoryDot cat={s.category} size="sm" />
                </span>
                <span className="wx-per-body">
                  <span className="wx-per-when ae-num">{whenOf(s.period)}</span>
                  {detail ? <span className="wx-per-detail">{detail}</span> : null}
                </span>
              </li>
            );
          })}
      </ul>
    </div>
  );
}
